// ─── Les échéances en ARBRE : année → mois → opérations ──────────
// Demandé par Cédric le 14/08. Une prévision mensuelle sans fin produit une
// liste qui s'allonge indéfiniment ; l'année est le repère naturel pour s'y
// retrouver.
//
// Chaque niveau porte son état PRÉVU / RÉEL / ÉCART. L'écart n'a pas de sens
// « bon » ou « mauvais » dans l'absolu : +30 € sur un salaire est une bonne
// nouvelle, la même somme sur une dépense n'en est pas une. C'est le SENS de
// la ligne qui décide de la couleur — le module de calcul, lui, ne tranche pas.
import React, { useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { LigneSwipe, useUI, useT } from '@mop/core';
import { formatEuro, montantOperation } from './services/calculs';
import { grouperParAnnee, anneeParDefaut } from './services/arbreEcheances';
import { statutDe, estClos } from './data/statuts';
import EnTeteArbre, { RETRAIT_ENFANTS } from './EnTeteArbre';

const HIT = { top: 8, bottom: 8, left: 8, right: 8 };

const libelleMois = (mk, t) => {
  const [, m] = String(mk || '').split('-').map(Number);
  const noms = t('commun.moisLongs');
  return (Array.isArray(noms) ? noms[m - 1] : '') || mk || '';
};

export default function ArbreEcheances({
  // `operationsDe(mois)` renvoie les OPÉRATIONS rattachées, pas un libellé
  // concaténé : ce sont les feuilles de l'arbre, chacune doit exister par
  // elle-même — avec sa date, son montant, et de quoi la rouvrir.
  occurrences = [], revenu = false, operationsDe, onEcheance, onOperation,
  onDetacher, onEditerOperation, onDeplacer,
  // Sur le MOIS lui-meme.
  onToutDetacher, onVerrouiller,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);

  const groupes = useMemo(() => grouperParAnnee(occurrences), [occurrences]);
  // L'année courante s'ouvre d'emblée : c'est celle qu'on vient consulter.
  // On mémorise les REPLIÉES, donc une année qui apparaît plus tard (parce
  // que l'horizon s'allonge) est ouverte, pas cachée.
  const [replies, setReplies] = useState(() => {
    const defaut = anneeParDefaut(grouperParAnnee(occurrences));
    return new Set(grouperParAnnee(occurrences)
      .map(g => g.annee).filter(a => a !== defaut));
  });
  // Les mois REPLIÉS. Ils sont donc OUVERTS par défaut : les opérations
  // rattachées sont le cœur de l'information — « les feuilles doivent être les
  // opérations en dessous de chaque mois » (Cédric, 14/08). Les cacher derrière
  // un chevron revenait à ne pas les montrer du tout.
  const [moisReplies, setMoisReplies] = useState(() => new Set());
  const basculerMois = (mk) => setMoisReplies((prev) => {
    const n = new Set(prev);
    if (n.has(mk)) n.delete(mk); else n.add(mk);
    return n;
  });
  const basculer = (a) => setReplies((prev) => {
    const n = new Set(prev);
    if (n.has(a)) n.delete(a); else n.add(a);
    return n;
  });

  // CONVENTION SIGNÉE (15/08) : l'écart est réel signé − prévu signé. Son
  // signe dit directement si c'est favorable — le calcul porte le sens, plus
  // besoin de le déduire du type de la ligne.
  const signeLigne = revenu ? 1 : -1;
  const couleurEcart = (ecart) => (ecart > 0
    ? { color: UI.ok || '#2e7d32' }
    : { color: UI.danger || '#c0392b' });

  if (!groupes.length) return null;

  return (
    <View>
      {groupes.map(g => {
        const plie = replies.has(g.annee);
        return (
          <View key={g.annee}>
            {/* Grammaire commune (15/08) — une année n'a pas d'emoji, le
                composant s'en passe. À droite : le prévu de l'année, et
                l'écart s'il y en a un — le réel seul ne dirait pas s'il
                est conforme. */}
            <EnTeteArbre plie={plie} onBasculer={() => basculer(g.annee)}
              titre={String(g.annee)} nb={g.nb}
              droite={(
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <Text style={s.anneePrevu}>{formatEuro(g.prevu)}</Text>
                  {Math.abs(g.ecart) >= 0.01 && (
                    <Text style={[s.anneeEcart, couleurEcart(signeLigne * g.ecart)]}>
                      {signeLigne * g.ecart > 0 ? '+' : ''}{formatEuro(signeLigne * g.ecart)}
                    </Text>
                  )}
                </View>
              )} />

            {!plie && g.mois.map(o => {
              const ecart = Math.round(signeLigne * (Math.abs(o.reel || 0) - Math.abs(o.prevu || 0)) * 100) / 100;
              const ops = operationsDe?.(o.mois) || [];
              const ouvert = !moisReplies.has(o.mois);
              return (
                <View key={o.mois} style={{ marginLeft: RETRAIT_ENFANTS }}>
                {/* Le MOIS se balaie aussi (Cedric, 14/08) : gauche → tout
                    detacher, droite → rattacher une operation de plus et
                    verrouiller. `onDeplacer` porte ici le VERROU : le Core
                    expose deux boutons a droite, pas trois, et le verrou est
                    la seconde action la plus utile sur un mois. */}
                <LigneSwipe espacement={0} enabled
                  onSupprimer={(onToutDetacher && ops.length) ? () => onToutDetacher(o) : undefined}
                  onEditer={onEcheance ? () => onEcheance(o) : undefined}
                  onDeplacer={onVerrouiller ? () => onVerrouiller(o) : undefined}>
                  {/* Grammaire commune (15/08) : le statut tient le rôle de
                      l'emoji, le corps ouvre le rattachement, et à droite le
                      RÉEL et l'écart — pas l'estimatif, identique douze mois
                      sur douze. Sur un mois sans réel, le prévu en retrait :
                      « 0,00 € » serait un constat qui n'a pas eu lieu. */}
                  <EnTeteArbre plie={!ouvert} sansChevron={!ops.length}
                    onBasculer={() => basculerMois(o.mois)}
                    emoji={statutDe(o).emoji} titre={libelleMois(o.mois, t)}
                    onCorps={onEcheance ? () => onEcheance(o) : undefined}
                    droite={(
                      <View style={{ alignItems: 'flex-end' }}>
                        {Math.abs(o.reel || 0) > 0.005 ? (
                          <>
                            <Text style={[s.moisPrevu, revenu && { color: UI.ok || '#2e7d32' }]}>
                              {montantOperation(o.reel, revenu ? 'revenu' : 'depense')}
                            </Text>
                            {Math.abs(ecart) >= 0.01 && (
                              <Text style={[s.moisReel, couleurEcart(ecart)]}>
                                {ecart > 0 ? '+' : ''}{formatEuro(ecart)}
                              </Text>
                            )}
                          </>
                        ) : (
                          <Text style={[s.moisPrevu, s.moisAttendu]}>
                            {montantOperation(o.prevu, revenu ? 'revenu' : 'depense')}
                          </Text>
                        )}
                      </View>
                    )} />
                </LigneSwipe>

                {/* LES FEUILLES : les opérations du compte qui honorent ce
                    mois. C'est le lien concret entre la prévision et le
                    relevé — chacune avec sa date et la part qu'elle porte. */}
                {ouvert && ops.map(op => (
                  /* Principe app-wide : le tap INFORME, le swipe AGIT.
                     Gauche → detacher (la part, pas l'operation) ; droite →
                     editer et deplacer vers un autre mois. `LigneSwipe` sait
                     deja poser deux boutons du meme cote — verifie avant
                     d'ecrire. */
                  <LigneSwipe key={op.id} espacement={5} enabled
                    onSupprimer={onDetacher ? () => onDetacher(op) : undefined}
                    onEditer={onEditerOperation ? () => onEditerOperation(op) : undefined}
                    onDeplacer={onDeplacer ? () => onDeplacer(op) : undefined}>
                  <TouchableOpacity style={s.feuille} activeOpacity={onOperation ? 0.7 : 1}
                    disabled={!onOperation} onPress={() => onOperation?.(op)}>
                    <Text style={s.feuilleIc}>🔗</Text>
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <Text style={s.feuilleLbl} numberOfLines={1}>{op.libelle || '—'}</Text>
                      {!!op.date && <Text style={s.feuilleDate}>{op.date}</Text>}
                    </View>
                    <Text style={s.feuilleMontant}>{formatEuro(op.montant)}</Text>
                  </TouchableOpacity>
                  </LigneSwipe>
                ))}
                </View>
              );
            })}
          </View>
        );
      })}
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  anneePrevu: { fontSize: 13.5, fontWeight: '800', color: UI.muted },
  anneeEcart: { fontSize: 12.5, fontWeight: '800', marginLeft: 8 },
  moisPrevu: { fontSize: 13.5, fontWeight: '800', color: UI.ink },
  moisReel: { fontSize: 11.5, color: UI.muted, marginTop: 1, fontWeight: '700' },
  // Un montant seulement ATTENDU se distingue d'un montant constate.
  moisAttendu: { color: UI.muted, fontWeight: '700' },
  // Retrait COMMUN (22) et non plus 34 : la feuille vit déjà dans le
  // conteneur du mois, lui-même en retrait — les niveaux s'additionnent.
  feuille: { flexDirection: 'row', alignItems: 'center', gap: 9, paddingVertical: 7, paddingHorizontal: 11, marginLeft: RETRAIT_ENFANTS, borderRadius: 9, borderWidth: 0.5, borderColor: UI.border, borderStyle: 'dashed' },
  feuilleIc: { fontSize: 13 },
  feuilleLbl: { fontSize: 12.5, fontWeight: '700', color: UI.ink },
  feuilleDate: { fontSize: 11, color: UI.faint, marginTop: 1 },
  feuilleMontant: { fontSize: 12.5, fontWeight: '800', color: UI.muted },
});
